/**
 * iter-119aa (P1 - SA universal scope filter) — global tenant/org scope
 * that injects into every joined-query request.
 *
 * Why a context, not URL state alone?
 *   • Multiple components read this on every render (every dashboard,
 *     every Sense AI session, the gallery, reports). Plumbing props was
 *     non-starter.
 *   • The scope MUST stay in URL params so it's shareable + survives
 *     refresh. The provider syncs URL ↔ state both directions.
 *
 * Visibility: only Super Admin role can read non-null values. For any
 * other role the provider returns `{tenant_id: null, org_id: null}`
 * unconditionally — guarding against accidental scope bleed if the role
 * changes mid-session.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";

const ScopeCtx = createContext({ tenantId: null, orgId: null, setScope: () => {} });

export function ScopeFilterProvider({ children }) {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const isSA = user?.role === "super_admin";

    const [scope, setScopeState] = useState(() => ({
        tenantId: isSA ? (params.get("scope_tenant") || null) : null,
        orgId: isSA ? (params.get("scope_org") || null) : null,
    }));

    // Re-read from URL when the user navigates (sidebar links keep params)
    // OR when the role changes (logout → login as different role).
    useEffect(() => {
        if (!isSA) {
            setScopeState({ tenantId: null, orgId: null });
            return;
        }
        setScopeState({
            tenantId: params.get("scope_tenant") || null,
            orgId: params.get("scope_org") || null,
        });
    }, [params, isSA]);

    const setScope = useCallback((next) => {
        if (!isSA) return;
        const merged = { ...scope, ...next };
        const sp = new URLSearchParams(params);
        if (merged.tenantId) sp.set("scope_tenant", merged.tenantId);
        else sp.delete("scope_tenant");
        if (merged.orgId) sp.set("scope_org", merged.orgId);
        else sp.delete("scope_org");
        setParams(sp, { replace: true });
        setScopeState(merged);
    }, [scope, isSA, params, setParams]);

    // iter-120 · expose a `clear()` shortcut so the empty-state banner
    // (or any other UI) can pop SA back to "all tenants" with one
    // click without having to construct the `{ tenantId: null,
    // orgId: null }` payload at every call site.
    const clear = useCallback(() => setScope({ tenantId: null, orgId: null }), [setScope]);

    return (
        <ScopeCtx.Provider value={{ ...scope, isSA, setScope, clear }}>
            {children}
        </ScopeCtx.Provider>
    );
}

export const useScopeFilter = () => useContext(ScopeCtx);

/**
 * Helper for API call sites — folds the scope into a joined-query body.
 * Returns the original body unchanged for non-SA users so the backend's
 * SA-only branch never fires for non-privileged callers.
 */
export function applyScopeFilter(body, scope) {
    if (!scope || !scope.isSA) return body;
    const next = { ...body };
    if (scope.tenantId) next.scope_tenant_id = scope.tenantId;
    if (scope.orgId) next.scope_org_id = scope.orgId;
    return next;
}
